import { AdCreative } from 'common/api/v1/types'
import { useEffect, useState } from 'react'
import { AdPodsRequestParams } from '../../../../api/nm-types'
import { useUser, withDefaultPagination } from '../../../../utils'
import { AppDispatch, GlobalState, useRoutes } from '../../../../store'
import { getSmallConfig } from '../../adPod/tableConfig'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { listAdPods } from '../../../../redux/actions/adPodActions'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Pendable from '../../../common/Pendable'
import MuiTable from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { notUndefinedOrNull } from 'common/api/v1/helpers'
import TableCell from '@mui/material/TableCell'
import { classes, styles } from '../styles'
import omit from 'lodash/fp/omit'
import Typography from '@mui/material/Typography'
import TableBody from '@mui/material/TableBody'
import Divider from '@mui/material/Divider'
import { Pagination } from '../../../common/Table'
import { Paper } from '../../../common/Form'
import { canEditAdPod } from '../../adPod/List'
import { useNavigate } from 'react-router'

export const AdPodTable = ({ adCreative }: { adCreative: AdCreative }) => {
  const [searchParams, setSearchParams] = useState<AdPodsRequestParams>(
    withDefaultPagination({} as AdPodsRequestParams),
  )
  const routes = useRoutes()
  const navigate = useNavigate()
  const user = useUser()
  const tableConfig = getSmallConfig({ user, routes }).filter((c) => c !== null)
  const dispatch = useDispatch<AppDispatch>()

  const { adPods, loading, total } = useSelector(
    ({ adPodReducer }: GlobalState) => ({
      adPods: adPodReducer.adPods,
      loading: adPodReducer.loading,
      total: adPodReducer.total,
    }),
    shallowEqual,
  )

  useEffect(() => {
    dispatch(listAdPods({ ...searchParams, adCreativeId: adCreative.id }))
  }, [searchParams])

  return (
    <Paper title={'Ad Pods'} className="outlined">
      <Box sx={{ height: '35vh', width: '100%', mt: 2, display: 'flex' }}>
        <Grid container spacing={2} sx={{ height: '100%', flexGrow: 1 }}>
          <Grid
            size={{ xs: 12 }}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Box sx={{ flexGrow: 1, overflowY: 'auto', borderRadius: '4px' }}>
              <Pendable pending={loading} cover={!!adPods.length} id="ad-pods-table">
                {adPods.length > 0 ? (
                  <MuiTable id="ad-pods-table" stickyHeader>
                    <TableHead
                      sx={{
                        '& .MuiTableCell-head': {
                          backgroundColor: 'background.paper',
                        },
                      }}
                    >
                      <TableRow>
                        {tableConfig.filter(notUndefinedOrNull).map(({ title = '', props, headerProps }, ind) => (
                          <TableCell
                            key={ind}
                            sx={{ root: styles.cell }}
                            padding={undefined}
                            data-table-column={title}
                            {...(headerProps ? headerProps : omit(['noTypography'], props))}
                          >
                            <Typography variant="body2" sx={classes.columnHeader}>
                              {title}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adPods.map((item, i) => (
                        <TableRow
                          hover
                          key={item.id}
                          onDoubleClick={() =>
                            canEditAdPod(user, item.createdBy)
                              ? void navigate(routes.adPodsUpdate({ id: item.id }))
                              : null
                          }
                        >
                          {tableConfig.filter(notUndefinedOrNull).map(({ getValue, props, title }) => (
                            <TableCell
                              {...omit(['noTypography'], props)}
                              data-cell-name={`${title}-cell`}
                              key={`${item.id}-${title}`}
                            >
                              <Typography component="div" variant="body2">
                                {getValue(item, i)}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </MuiTable>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography>No pods.</Typography>
                  </Box>
                )}
              </Pendable>
            </Box>
            <Box sx={{ flexShrink: 0, mt: 2 }}>
              <Divider />
              <Pagination
                id="ad-pods-table-pagination"
                total={total}
                page={searchParams.pageNumber || '0'}
                perPage={searchParams.rowsPerPage || '10'}
                changePageCallback={(page: string) => {
                  setSearchParams((prev) => ({ ...prev, pageNumber: page }))
                }}
                changeRowsPerPageCallback={(rowsPerPage: string) => {
                  setSearchParams((prev) => ({ ...prev, pageNumber: '0', rowsPerPage }))
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}
